import React from 'react';
import Chart from 'react-apexcharts/dist/react-apexcharts.esm';
import { useTranslation } from 'react-i18next';
import { select } from '@panel/commons/core/themev2';
import { formatAmount } from '@panel/commons/helpers/formatAmount';
import { ChartsWrapper, ChartContainer } from './styles';
import './reportTooltipStyles.css';

const ReportsChart = ({ device, reportsData, inColumn }) => {
  const { newSeries, viewedSeries, categories, min, max } = reportsData;
  const { t } = useTranslation();

  const chartConfig = {
    series: [
      {
        name: t('panel.estadisticas.reports.new'),
        data: (newSeries && newSeries.length > 0 && newSeries) || [0],
        color: select('purple')
      },
      {
        name: t('panel.estadisticas.reports.viewed'),
        data: (viewedSeries && viewedSeries.length > 0 && viewedSeries) || [0],
        color: select('purpleLight')
      }
    ],
    key: 'reports',
    options: {
      states: {
        normal: {
          filter: {
            type: 'none',
            value: 0.1
          }
        },
        hover: {
          filter: {
            type: 'lighten',
            value: 0.1
          }
        },
        active: {
          allowMultipleDataPointsSelection: true,
          filter: {
            type: 'none',
            value: 0
          }
        }
      },
      fill: {
        colors: [select('grayMedium')],
        opacity: 1,
        type: 'solid'
      },
      chart: {
        stacked: true,
        animations: {
          enabled: true,
          easing: 'easeout',
          speed: 1000,
          animateGradually: {
            enabled: false,
            delay: 10
          },
          dynamicAnimation: {
            enabled: true,
            speed: 1000
          }
        },
        toolbar: {
          show: false
        },
        zoom: {
          enabled: false
        }
      },
      plotOptions: {
        bar: {
          columnWidth: '22%',
          horizontal: false
        }
      },
      xaxis: {
        categories: (categories && categories) || [],
        tickPlacement: 'between',
        labels: {
          show: true,
          rotate: 0,
          rotateAlways: false,
          offsetX: 0,
          offsetY: -5,
          style: {
            colors: categories ? select('grayMediumDark') : select('white'),
            fontSize: '12px',
            fontFamily: 'Hind',
            fontWeight: 500
          },
          formatter: value => {
            if (!value) return [];
            const text = value && t(`panel.estadisticas.reports.${value}`).toString();
            const splitText = text.split(' ');
            if (categories && categories.length === 1) return [t(`panel.estadisticas.reports.${value}`)];
            if (
              categories &&
              categories.length > 1 &&
              categories.length <= 3 &&
              splitText.length > 2 &&
              splitText[0].length + splitText[1].length + splitText[2].length < 18 &&
              device !== 'tablet'
            ) {
              return [
                `${splitText[0]} ${splitText[1]} ${splitText[2]}`,
                ` ${splitText[3] ? splitText[3] : ''} ${splitText[4] ? splitText[4] : ''}${
                  splitText[4] && !splitText[5] ? '' : '...'
                }`
              ];
            }
            if (splitText.length === 2) {
              return [`${splitText[0]}`, ` ${splitText[1]}`];
            }
            if (splitText.length > 2 && splitText[0].length + splitText[1].length > 9) {
              return [
                `${splitText[0]}`,
                ` ${splitText[1] ? splitText[1] : ''}${splitText[1] && !splitText[2] ? '' : '...'}`
              ];
            }
            if (
              categories &&
              categories.length > 0 &&
              splitText.length > 2 &&
              splitText[0].length + splitText[1].length + splitText[2].length < 10
            ) {
              return [
                `${splitText[0]} ${splitText[1]} ${splitText[2]}`,
                ` ${splitText[3] ? splitText[3] : ''}${splitText[3] && !splitText[4] ? '' : '...'}`
              ];
            }
            if (
              categories &&
              categories.length > 0 &&
              splitText.length > 2 &&
              splitText[0].length + splitText[1].length < 10
            ) {
              return [
                `${splitText[0]} ${splitText[1]}`,
                ` ${splitText[2] ? splitText[2] : ''}${splitText[2] && !splitText[3] ? '' : '...'}`
              ];
            }
            return categories && categories.length > 0 ? [t(`panel.estadisticas.reports.${value}`)] : '';
          }
        },
        axisBorder: {
          width: categories && categories.length === 2 && device !== 'tablet' ? '154%' : '105%',
          show: true,
          offsetX: categories && categories.length === 2 && device !== 'tablet' ? -178 : -18
        },
        axisTicks: {
          show: false
        },
        tooltip: {
          enabled: false
        }
      },
      yaxis: {
        min: min || 0,
        max: max || 4,
        forceNiceScale: true,
        labels: {
          show: true,
          style: {
            colors: select('grayMediumDark'),
            fontSize: '12px',
            fontFamily: 'Hind',
            fontWeight: 500
          },
          offsetX: -5,
          formatter: value => formatAmount(value)
        },
        axisBorder: {
          show: true,
          offsetX: -5,
          offsetY: -1
        },
        axisTicks: {
          show: false
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        show: true,
        width: 0,
        curve: 'straight'
      },
      grid: {
        show: false,
        borderColor: select('grayMedium'),
        strokeDashArray: 5,
        position: 'back',
        xaxis: {
          lines: {
            show: false
          }
        },
        yaxis: {
          lines: {
            show: false
          }
        },
        padding: {
          top: 0,
          right: categories && categories.length === 2 && device !== 'tablet' ? 180 : 20,
          bottom: 0,
          left: categories && categories.length === 2 && device !== 'tablet' ? 180 : 20
        }
      },
      tooltip: {
        enabled: true,
        followCursor: true,
        shared: false,
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const value = series[seriesIndex][dataPointIndex];
          const nameSerie = w.globals.seriesNames[seriesIndex];
          const colorSerie = w.globals.markers.colors[seriesIndex];
          const color = colorSerie.replace('#', '');
          return `<div class="arrow_box" data-panel="estadisticas-chart-tooltip">
                  <div class="text_container">
                  <span class="markerNew_${color}" data-panel="estadisticas-reportChart-tooltip">
                    ${t(`panel.estadisticas.reports.${categories[dataPointIndex]}`)}
                    <br/>
                    <div class="text_serie" data-panel="estadisticas-reportChart-tooltip-value">(${value} ${
            value === 1 ? nameSerie.toLowerCase().replace('os', 'o') : nameSerie.toLowerCase()
          })</div>
                  </span>
                  </div>
                </div>`;
        },
        onDatasetHover: {
          highlightDataSeries: false
        }
      },
      legend: {
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '12px',
        fontFamily: 'Hind',
        fontWeight: 500,
        offsetY: 3,
        labels: {
          colors: select('grayDarkLight')
        },
        markers: {
          width: 11,
          height: 11,
          radius: 12,
          offsetX: -2,
          offsetY: -1
        },
        onItemClick: {
          toggleDataSeries: false
        },
        itemMargin: {
          horizontal: 15
        },
        onItemHover: {
          highlightDataSeries: false
        }
      }
    }
  };

  return (
    <ChartsWrapper device={device} directionColumn={inColumn}>
      <ChartContainer device={device} directionColumn={inColumn}>
        <Chart
          type='bar'
          options={chartConfig.options}
          series={chartConfig.series}
          key={chartConfig.key}
          width='100%'
          height='100%'
        />
      </ChartContainer>
    </ChartsWrapper>
  );
};

export default ReportsChart;
